'use client';

import React, { useState } from 'react';
import Link from 'next/link';

interface NavItem {
  title?: string;
  label?: string;
  url: string;
}

interface HeaderData {
  title?: string;
  phone?: string;
  cta_text?: string;
  cta_url?: string;
  menu_items?: Array<{ label: string; url: string }>;
}

interface LanguageSettings {
  active_locale?: string;
  default_locale?: string;
  enabled_locales?: string[];
  show_switcher_header?: boolean;
  show_switcher_footer?: boolean;
}

interface GlobalHeaderProps {
  header?: HeaderData;
  navigation?: NavItem[];
  siteName?: string;
  currentPath?: string;
  languageSettings?: LanguageSettings;
  translations?: Record<string, string>;
  urlPrefix?: string;
}

export function GlobalHeader({
  header,
  navigation = [],
  siteName = 'Volosyni',
  currentPath = '/',
  languageSettings,
  translations = {},
  urlPrefix = '',
}: GlobalHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const brandTitle = header?.title || siteName;
  const activeLocale = languageSettings?.active_locale || languageSettings?.default_locale || 'uk';
  const enabledLocales = languageSettings?.enabled_locales || ['uk', 'en'];
  const showLanguageSwitcher =
    (languageSettings?.show_switcher_header ?? true) && enabledLocales.length > 1;

  const tr = (key: string, fallback: string) => translations[key] || fallback;

  // Determine menu items: user-defined items take priority, fallback to published pages
  const menuItems: Array<{ label: string; url: string }> =
    header?.menu_items && header.menu_items.length > 0
      ? header.menu_items
      : navigation.map((p) => ({
          label: p.title || p.label || tr('header.home', 'Сторінка'),
          url: p.url,
        }));

  const buildUrlWithLang = (url: string, lang?: string) => {
    // If external URL or anchor
    if (!url.startsWith('/') || url.startsWith('//')) {
      return url;
    }

    const defaultLang = languageSettings?.default_locale || 'uk';
    const targetLang = lang || activeLocale;
    const cleanPath = url === '/' ? '' : url.replace(/^\/+/, '');
    const base = urlPrefix ? urlPrefix.replace(/\/+$/, '') : '';

    if (urlPrefix) {
      // In preview mode: keep base preview route and use query param ?locale= if non-default
      const pathPart = cleanPath ? `/${cleanPath}` : '';
      if (!targetLang || targetLang === defaultLang) {
        return `${base}${pathPart}` || '/';
      }
      return `${base}${pathPart}?locale=${targetLang}`;
    }

    if (!targetLang || targetLang === defaultLang) {
      return cleanPath ? `/${cleanPath}` : '/';
    }

    return cleanPath ? `/${targetLang}/${cleanPath}` : `/${targetLang}`;
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-xs transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Brand Logo / Title */}
          <div className="flex-shrink-0">
            <Link
              href={buildUrlWithLang('/')}
              className="text-xl sm:text-2xl font-black tracking-tight text-gray-950 hover:text-blue-600 transition-colors"
            >
              {brandTitle}
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          {menuItems.length > 0 && (
            <nav className="hidden md:flex items-center space-x-8">
              {menuItems.map((item, idx) => {
                const isActive = currentPath === item.url || (item.url !== '/' && currentPath.startsWith(item.url));
                return (
                  <Link
                    key={idx}
                    href={buildUrlWithLang(item.url)}
                    className={`text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-blue-600 font-semibold'
                        : 'text-gray-600 hover:text-gray-950'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          )}

          {/* Right Action: Language Switcher, Phone and/or CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            {showLanguageSwitcher && (
              <div className="flex items-center bg-gray-100 rounded-full p-1 text-xs font-semibold">
                {enabledLocales.map((loc) => {
                  const isCurrent = loc === activeLocale;
                  return (
                    <Link
                      key={loc}
                      href={buildUrlWithLang(currentPath, loc)}
                      className={`px-2.5 py-1 rounded-full uppercase transition-all ${
                        isCurrent
                          ? 'bg-white text-blue-600 shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {loc}
                    </Link>
                  );
                })}
              </div>
            )}

            {header?.phone && (
              <a
                href={`tel:${header.phone.replace(/\s+/g, '')}`}
                className="text-sm font-semibold text-gray-700 hover:text-blue-600 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {header.phone}
              </a>
            )}

            {header?.cta_text && (
              <a
                href={header.cta_url || '#order'}
                className="btn-primary text-sm px-4 py-2"
              >
                {header.cta_text}
              </a>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center space-x-2">
            {showLanguageSwitcher && (
              <div className="flex items-center bg-gray-100 rounded-full p-0.5 text-xs font-semibold">
                {enabledLocales.map((loc) => (
                  <Link
                    key={loc}
                    href={buildUrlWithLang(currentPath, loc)}
                    className={`px-2 py-0.5 rounded-full uppercase ${
                      loc === activeLocale ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-600'
                    }`}
                  >
                    {loc}
                  </Link>
                ))}
              </div>
            )}

            {header?.phone && (
              <a
                href={`tel:${header.phone.replace(/\s+/g, '')}`}
                className="p-2 text-gray-700 hover:text-blue-600"
                aria-label={tr('header.call_us', 'Зателефонувати')}
              >
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </a>
            )}

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none"
              aria-label={tr('header.menu', 'Меню')}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 pt-3 pb-6 space-y-3">
          {menuItems.map((item, idx) => {
            const isActive = currentPath === item.url || (item.url !== '/' && currentPath.startsWith(item.url));
            return (
              <Link
                key={idx}
                href={buildUrlWithLang(item.url)}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-base font-medium rounded-md px-3 ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </Link>
            );
          })}

          {header?.cta_text && (
            <div className="pt-2">
              <a
                href={header.cta_url || '#order'}
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center btn-primary py-3"
              >
                {header.cta_text}
              </a>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
